import {
  generateBarcodeDataUrl,
  parseJsonObject,
  parseOrderAddress,
  parseOrderCargoInfo,
  resolveCargoLogoSrc,
  toTurkishUpper,
} from './trendyolPrintUtils';

export const HB_ORANGE = '#ff6000';
export const HB_LABEL_BORDER = '#333333';

export { generateBarcodeDataUrl, parseJsonObject, parseOrderAddress, parseOrderCargoInfo, resolveCargoLogoSrc, toTurkishUpper };

export const deepFindString = (obj: any, keys: string[], depth = 0): string => {
  if (!obj || typeof obj !== 'object' || depth > 6) return '';
  for (const key of Object.keys(obj)) {
    if (keys.some((target) => target.toLowerCase() === key.toLowerCase())) {
      const value = obj[key];
      if (value !== undefined && value !== null && typeof value !== 'object' && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
  }
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value && typeof value === 'object') {
      const found = deepFindString(Array.isArray(value) ? { ...value } : value, keys, depth + 1);
      if (found) return found;
    }
  }
  return '';
};

export const normalizeHepsiburadaAddress = (raw: any) => {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const value = raw[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };
  const name = pick('fullName', 'name', 'recipientName');
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  const district = pick('town', 'townName', 'county', 'district', 'districtName');
  const neighborhood = pick('neighborhood', 'district', 'districtName');
  return {
    name: name || firstLast,
    address: pick('address', 'fullAddress', 'address1', 'addressLine'),
    city: pick('city', 'cityName'),
    district,
    neighborhood: neighborhood !== district ? neighborhood : '',
    phone: pick('phone', 'gsm', 'phoneNumber', 'alternatePhoneNumber'),
    postalCode: pick('postalCode', 'zipCode'),
    taxNumber: pick('taxNumber', 'taxId', 'vkn'),
    taxOffice: pick('taxOffice', 'taxHouse'),
    tcId: pick('tcId', 'identityNo', 'turkishIdentityNumber'),
  };
};

export const stripHbOrderPrefix = (orderNumber?: string) =>
  String(orderNumber || '')
    .replace(/^HB-/i, '')
    .trim();

export const resolveHepsiburadaBarcodeValue = (order: any, parsedCargoInfo: Record<string, any>) => {
  if (!order) return '';
  return (
    parsedCargoInfo?.barcode ||
    parsedCargoInfo?.cargoBarcode ||
    parsedCargoInfo?.trackingInfoCode ||
    parsedCargoInfo?.cargoTrackingNumber ||
    order.trackingNumber ||
    order.cargoBarcode ||
    deepFindString(parsedCargoInfo, ['barcode', 'trackingInfoCode', 'cargoTrackingNumber']) ||
    ''
  )
    .toString()
    .trim();
};

export const resolveHepsiburadaPackageNumber = (order: any, parsedCargoInfo: Record<string, any>) => {
  const fromCargo =
    parsedCargoInfo?.packageNumber ||
    parsedCargoInfo?.packagenumber ||
    deepFindString(parsedCargoInfo, ['packageNumber', 'packagenumber']);
  const fromOrder = stripHbOrderPrefix(order?.n11OrderId || order?.orderNumber);
  return String(fromCargo || fromOrder || '').trim();
};

export const resolveHepsiburadaDesi = (order: any, parsedCargoInfo: Record<string, any>) => {
  const raw =
    parsedCargoInfo?.deci ??
    parsedCargoInfo?.desi ??
    deepFindString(parsedCargoInfo, ['deci', 'desi']);
  if (raw === undefined || raw === null || String(raw).trim() === '') return '-';
  const numeric = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(numeric)) return String(raw);
  return numeric % 1 === 0 ? String(numeric) : numeric.toFixed(1);
};

export const resolveDeliveryTypeLabel = (parsedCargoInfo: Record<string, any>) => {
  const deliveryType =
    deepFindString(parsedCargoInfo, [
      'deliveryType',
      'deliveryOption',
      'shippingOption',
      'cargoOption',
      'deliveryModel',
    ]) || '';
  if (/yarin|yarın|kap[iı]nda/i.test(deliveryType)) {
    return 'Yarın Kapında Teslimatidir!';
  }
  if (deliveryType) return `${toTurkishUpper(deliveryType)} Teslimatidir!`;
  return '';
};

export const formatLabelDateTime = (value?: string | number) => {
  if (!value) return { date: '-', time: '-' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: '-', time: '-' };
  return {
    date: parsed.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    time: parsed.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
  };
};

export const buildFullAddressLine = (address: ReturnType<typeof normalizeHepsiburadaAddress>) => {
  if (!address) return '-';
  const parts = [
    address.address,
    address.neighborhood,
    address.district,
    address.city,
  ].filter(Boolean);
  return parts.join(' ') || '-';
};
